// @flow
import formatValue from "../../../utils/number/formatValue";
import { parseToNumber } from "../../../utils/number/parseNumbers";

import type { IConstraintModel } from "../../types";

const INEXACT_NUMBER_ID = "Constraint.Number.InexactNumber";
const OUT_OF_RANGE_ID = "Constraint.Number.OutOfRange";
const BELOW_MINIMUM_ID = "Constraint.Number.BelowMinimum";
const ABOVE_MAXIMUM_ID = "Constraint.Number.AboveMaximum";

/**
 */
class NumberBoundaryConstraint implements IConstraintModel {
  _minNumber: ?number;
  _maxNumber: ?number;
  _groupingSeparator: string = ",";
  _decimalSeparator: string = ".";
  _format: string = "";

  /**
   */
  constructor(
    minNumber: ?number = null,
    maxNumber: ?number = null,
    groupingSeparator: string,
    decimalSeparator: string,
    format: string | null,
  ) {
    this._format = format ?? "";
    this._groupingSeparator = groupingSeparator;
    this._decimalSeparator = decimalSeparator;
    this._minNumber = minNumber;
    this._maxNumber = maxNumber;
  }

  /**
   */
  get id(): string {
    if (
      this.minNumber !== null &&
      this.maxNumber !== null &&
      this.minNumber === this.maxNumber
    ) {
      return INEXACT_NUMBER_ID;
    } else if (this.minNumber !== null && this.maxNumber !== null) {
      return OUT_OF_RANGE_ID;
    } else if (this.minNumber !== null) {
      return BELOW_MINIMUM_ID;
    } else if (this.maxNumber !== null) {
      return ABOVE_MAXIMUM_ID;
    }

    return "Constraint.Number.NoBoundaryConstraint";
  }

  /**
   */
  get minNumber(): ?number {
    return this._minNumber;
  }

  /**
   */
  get maxNumber(): ?number {
    return this._maxNumber;
  }

  /**
   */
  get format(): string {
    return this._format;
  }

  /**
   */
  get groupingSeparator(): string {
    return this._groupingSeparator;
  }

  /**
   */
  get decimalSeparator(): string {
    return this._decimalSeparator;
  }

  /**
   */
  hasValidation(): boolean {
    return true;
  }

  /**
   */
  get defaultMessage(): string {
    switch (this.id) {
      case INEXACT_NUMBER_ID:
        return "Number must be precisely ${min-number}"; // NOSONAR
      case OUT_OF_RANGE_ID:
        return "Must be between ${min-number} and ${max-number}"; // NOSONAR
      case BELOW_MINIMUM_ID:
        return "The number must be larger than ${min-number}"; // NOSONAR
      case ABOVE_MAXIMUM_ID:
        return "The number must be smaller than ${max-number}"; // NOSONAR
      default:
        return "";
    }
  }

  /**
   */
  get parameters(): {
    "max-number": null | string,
    "min-number": null | string,
  } {
    return {
      "min-number":
        this.minNumber === null
          ? null
          : formatValue(
              this.minNumber,
              this.format,
              this.groupingSeparator,
              this.decimalSeparator,
            ),
      "max-number":
        this.maxNumber === null
          ? null
          : formatValue(
              this.maxNumber,
              this.format,
              this.groupingSeparator,
              this.decimalSeparator,
            ),
    };
  }

  /**
   */
  isExactNumber(value: string): boolean {
    const parsedNumber = parseToNumber(
      value,
      this.groupingSeparator,
      this.decimalSeparator,
    );
    return parsedNumber === this.minNumber;
  }

  /**
   */
  isSameOrAboveMinNumber(value: string): boolean {
    const parsedNumber = parseToNumber(
      value,
      this.groupingSeparator,
      this.decimalSeparator,
    );

    if (isNaN(parsedNumber) || parsedNumber == null) {
      return false;
    }

    return this.minNumber != null && parsedNumber >= this.minNumber;
  }

  /**
   */
  isSameOrBelowMaxNumber(value: string): boolean {
    const parsedNumber = parseToNumber(
      value,
      this.groupingSeparator,
      this.decimalSeparator,
    );

    if (isNaN(parsedNumber) || parsedNumber == null) {
      return false;
    }

    return this.maxNumber != null && parsedNumber <= this.maxNumber;
  }

  /**
   */
  isBetweenNumbers(value: string): boolean {
    return (
      this.isSameOrAboveMinNumber(value) && this.isSameOrBelowMaxNumber(value)
    );
  }

  /**
   */
  validate(value: string): boolean {
    switch (this.id) {
      case INEXACT_NUMBER_ID:
        return this.isExactNumber(value);
      case OUT_OF_RANGE_ID:
        return this.isBetweenNumbers(value);
      case BELOW_MINIMUM_ID:
        return this.isSameOrAboveMinNumber(value);
      case ABOVE_MAXIMUM_ID:
        return this.isSameOrBelowMaxNumber(value);
      default:
        return true;
    }
  }

  /**
   */
  get isMandatoryConstraint(): boolean {
    return false;
  }
}

export default NumberBoundaryConstraint;
